import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import toast from 'react-hot-toast';
import ShareReferralPanel from './ShareReferralPanel';
import ProgramReferralCard from './ProgramReferralCard';
import PageHeader from '../ui/PageHeader';
import UserAvatar from '../ui/UserAvatar';
import { LinkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { mapDualProgramLinks, referralUrlForLink } from '../../utils/referralLinks';
import { CardGridSkeleton } from '../ui/PageSkeleton';

function VendorReferralPromoView({ user: userProp }) {
  const { t, formatMessage } = useLanguage();
  const { user: authUser } = useAuth();
  const user = authUser || userProp;
  const e = t.enrola;
  const p = e.promoView;
  const [referralLinks, setReferralLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadReferralLinks() {
      setLoadingLinks(true);
      try {
        const response = await axios.get('/api/referral-links', { withCredentials: true });
        if (!cancelled) {
          setReferralLinks(response.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading vendor referral links:', error);
          toast.error(t.dashboard?.errorLoadingReferralLinks || 'Error loading referral links');
        }
      } finally {
        if (!cancelled) {
          setLoadingLinks(false);
        }
      }
    }

    loadReferralLinks();
    return () => {
      cancelled = true;
    };
  }, [t.dashboard?.errorLoadingReferralLinks]);

  const { clientsLink, partnersLink } = mapDualProgramLinks(referralLinks);
  const urlClients = referralUrlForLink(clientsLink);
  const urlPartners = referralUrlForLink(partnersLink);

  return (
    <div className="space-y-6 xl:space-y-7">
      <PageHeader
        eyebrow={p.eyebrow}
        icon={LinkIcon}
        title={e.promoCard}
        subtitle={p.subtitle}
      />

      {loadingLinks ? (
        <CardGridSkeleton cards={2} />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start">
          <div className="space-y-4">
            <motion.div
              layout
              className="card-premium p-6 overflow-hidden"
            >
              <h3 className="text-lg font-semibold text-secondary-900  mb-4">
                {p.profilePhoto}
              </h3>
              <div className="flex flex-col sm:flex-row gap-6 items-center">
                <div className="relative flex-shrink-0 overflow-hidden rounded-3xl border-4 border-white shadow-glow ">
                  <UserAvatar
                    src={user?.avatar_url}
                    alt={user?.username || p.vendorFallback}
                    size="xl"
                  />
                </div>
                <div className="flex-1 space-y-3">
                  <p className="text-xs text-secondary-500">
                    {p.settingsPhotoHint || 'Update your profile photo in Settings → Profile.'}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-premium p-0 overflow-hidden border-2 border-orange-200/60"
            >
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4 text-white flex justify-between items-center">
                <div>
                  <p className="text-sm opacity-90">{p.promoter}</p>
                  <p className="text-xl font-bold">{user?.username || p.vendorFallback}</p>
                </div>
                <UserAvatar
                  src={user?.avatar_url}
                  alt={user?.username || p.vendorFallback}
                  size="md"
                  className="border border-white/30 shadow-soft"
                />
              </div>
              <div className="p-6 grid grid-cols-1 gap-4 bg-gradient-to-b from-white to-orange-50/30   sm:grid-cols-2">
                <div className="rounded-2xl bg-white border border-orange-100 p-3 text-center shadow-soft  ">
                  <p className="text-xs font-semibold text-orange-700 mb-2 ">
                    {e.programClientsLabel}
                  </p>
                  <div className="flex justify-center">
                    <QRCodeSVG
                      value={urlClients}
                      size={96}
                      level="H"
                      fgColor="#c2410c"
                      bgColor="#ffffff"
                    />
                  </div>
                </div>
                <div className="rounded-2xl bg-white border border-violet-100 p-3 text-center shadow-soft  ">
                  <p className="text-xs font-semibold text-violet-700 mb-2 ">
                    {e.programPartnersLabel}
                  </p>
                  <div className="flex justify-center">
                    <QRCodeSVG
                      value={urlPartners}
                      size={96}
                      level="H"
                      fgColor="#5b21b6"
                      bgColor="#ffffff"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="space-y-6">
            <ProgramReferralCard programKey="clients" link={clientsLink} />
            <ProgramReferralCard programKey="partners" link={partnersLink} />
            <ShareReferralPanel
              url={urlClients}
              message={formatMessage(p.defaultShareMessage, { url: urlClients })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorReferralPromoView;
